import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';

export const Audio = styled.audio`
  opacity: 0;
  position: absolute;
  left: -5000px;
  top: -5000px;
  width: 1px;
  height: 1px;
`;

export const Player = styled.div`
  position: relative;
  overflow: hidden;
  padding: 16px;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  gap: 16px;
  margin-top: 12px;
`;

export const PlayButton = styled.button`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  transition: 0.15s ease-out background-color;
  &:hover {
    background: ${({ theme }) => theme.colors.lightGray};
  }
`;

export const DownloadButton = styled('a').attrs({
  target: '_blank',
  rel: 'noreferrer noopener',
})`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};

  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

interface TrackProps {
  $playedPercentage: number;
}

export const Track = styled.div<TrackProps>`
  position: relative;
  flex: 1;
  height: 7px;
  border-radius: 100px;
  background: ${({ theme }) => theme.colors.lightGray};
  cursor: pointer;

  &:before {
    content: '';
    left: 0;
    position: absolute;
    transition: width 0.25s linear;
    width: ${({ $playedPercentage }) => $playedPercentage}%;
    max-width: 100%;
    border-radius: 100px;
    height: 100%;
    background: ${({ theme }) => theme.colors.purple};
  }
`;

export const TimePlayed = styled(Text)`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 2;
`;

export const TotalTime = styled(TimePlayed)`
  left: initial;
  right: 0;
`;

export const calculateTime = (secs: number) => {
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60);
  const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:${returnedSeconds}`;
};

interface Props {
  type: string;
  src: string;
}

const AudioPlayer: FC<Props> = ({ src, type }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number>();
  const [currentTime, setCurrentTime] = useState(0);

  const playedPercentage = !duration ? 0 : (currentTime / duration) * 100;

  const togglePlay = () => {
    isPlaying ? ref.current?.pause() : ref.current?.play();

    setIsPlaying((prev) => !prev);
  };

  const onTrackClick = (e: MouseEvent<HTMLDivElement>) => {
    const leftOffset = e.clientX - e.currentTarget.getBoundingClientRect().left;

    const percentage = leftOffset / e.currentTarget.clientWidth;

    ref.current!.currentTime = ref.current!.duration * percentage;
  };

  useEffect(() => {
    const { current } = ref;

    if (!current) {
      return;
    }

    const loadedmetadataCallback = () => {
      setDuration(current.duration);
    };

    const timeupdateCallback = () => {
      setCurrentTime(current.currentTime);

      if (current.currentTime >= current.duration) {
        setIsPlaying(false);
      }
    };

    current.addEventListener('loadedmetadata', loadedmetadataCallback);

    current.addEventListener('timeupdate', timeupdateCallback);

    return () => {
      current.removeEventListener('loadedmetadata', loadedmetadataCallback);
      current.removeEventListener('timeupdate', timeupdateCallback);
    };
  }, []);

  return (
    <Player>
      <PlayButton type="button" onClick={togglePlay}>
        {isPlaying ? (
          <svg
            width="6"
            height="8"
            viewBox="0 0 6 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="2" height="8" fill="currentColor" />
            <rect x="4" width="2" height="8" fill="currentColor" />
          </svg>
        ) : (
          <svg
            width="9"
            height="10"
            viewBox="0 0 9 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.33301 4.9987L0.333008 9.66536L0.333008 0.332031L8.33301 4.9987Z"
              fill="currentColor"
            />
          </svg>
        )}
      </PlayButton>
      <Track $playedPercentage={playedPercentage} onClick={onTrackClick}>
        {!!duration && (
          <>
            <TimePlayed $type="extraSmall" as="div">
              {calculateTime(currentTime)}
            </TimePlayed>
            <TotalTime $type="extraSmall" as="div">
              {calculateTime(duration)}
            </TotalTime>
          </>
        )}
      </Track>
      <DownloadButton href={src}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 7.00195L8 10.002L11 7.00195"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 2V10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.8569 8.85938V14.0022C14.8569 14.1538 14.7967 14.2991 14.6895 14.4063C14.5823 14.5135 14.437 14.5737 14.2854 14.5737H1.71401C1.56245 14.5737 1.41711 14.5135 1.30995 14.4063C1.20278 14.2991 1.14258 14.1538 1.14258 14.0022V8.85938"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </DownloadButton>
      <Audio ref={ref} preload="metadata">
        <source src={src} type={type} />
      </Audio>
    </Player>
  );
};

export default AudioPlayer;
